<template>
  <div class="card" ref="question">
    <p class="question-header">
      Question {{ cardIndex + 1 }} of {{ numQuestions }}
    </p>

    <p class="slate-graf question-text" v-html="question"></p>

    <ul ref="quizCard" class="answers">
      <li v-for="answer in shuffledAnswers" :key="answer.index">
        <button
          @click.passive="handleAnswer(answer.index)"
          :class="[answerClass(answer.index), 'answer-button']"
          :disabled="!active"
        >
          <span v-html="answer.text"></span>
          <Check v-if="answerClass(answer.index) === 'correct'" />
          <Cross v-if="answerClass(answer.index) === 'incorrect'" />
        </button>
      </li>
    </ul>

    <!-- todo: try to move all the rest of this back out of the question view  -->
    <Timer
      v-show="active"
      :active="active"
      :stopped="!!answerSelected"
      @stop="endRound"
    ></Timer>

    <div v-if="!active">
      <div v-if="userCorrect" class="correctness">
        <Check class="icon" />
        <strong>Correct!</strong>
      </div>
      <div v-else class="correctness">
        <Cross class="icon" />
        <strong>Wrong!</strong>
      </div>
      <p v-html="explainer" v-if="explainer" class="slate-graf explainer"></p>

      <HighFive
        v-if="last"
        :questionElement="$refs.question"
        :quizAlreadyCompleted="quizAlreadyCompleted"
      />

      <div class="next" :class="{ last }" v-if="current && !last">
        <button class="slate-button" @click="advance">Next question</button>
        <div class="score">
          <Score :questionScore="questionScore" :totalScore="totalScore" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Timer from "./Timer.vue";
import Check from "./Check.vue";
import Cross from "./Cross.vue";
import Score from "./Score.vue";
import HighFive from "./HighFive.vue";
import {
  getParentPositionInfo,
  scrollParentBy,
  scrollElIntoView,
} from "../utils.js";

export default {
  name: "Card",
  components: {
    Timer,
    Check,
    Cross,
    HighFive,
    Score,
  },
  emits: ["answer-selected", "next-clicked"],
  props: {
    numQuestions: { type: Number, required: true },
    cardIndex: { type: Number, required: true },
    question: { type: String, required: true },
    answers: { type: Array, required: true },
    correctAnswerIndex: { type: Number, default: -1 },
    selectedAnswer: { type: Object },
    explainer: { type: String, required: true },
    current: { type: Boolean, required: true },
    totalScore: { type: Number, required: true },
    quizAlreadyCompleted: { type: Boolean, required: true },
  },
  data() {
    const data = {
      selectedAnswerIndex: -1,
      timeUp: false,
      questionScore: -1,
    };
    if (this.selectedAnswer) {
      data.selectedAnswerIndex = this.selectedAnswer.answer;
      data.timeUp = true;
      data.questionScore = this.selectedAnswer.score;
    }
    return data;
  },
  computed: {
    last() {
      return this.cardIndex + 1 === this.numQuestions;
    },
    answerSelected() {
      return this.selectedAnswerIndex !== -1;
    },
    // whether the round is active / still waiting for an answer / timer still counting down
    active() {
      return !(this.answerSelected || this.timeUp);
    },
    userCorrect() {
      if (!this.validityDefined) return null;
      else return this.correctAnswerIndex === this.selectedAnswerIndex;
    },
    validityDefined() {
      return this.correctAnswerIndex !== undefined;
      // checks that backend has sent answer validity data to this.correctAnswers
      // to avoid displaying invalid answer responses in the split second prior to data receival
      // because userCorrect's default is false, so wrong-answer response is displayed briefly
    },
    shuffledAnswers() {
      return shuffle(
        //  we've been storing user answers in the past as an index rather than a text value,
        // so we need to preserve the original index before shuffling to maintain that format
        this.answers.map((answer, index) => ({ ...answer, index }))
      );
    },
  },
  methods: {
    endRound(score) {
      this.timeUp = true;
      this.questionScore =
        this.selectedAnswerIndex === this.correctAnswerIndex ? score : 0;

      this.$emit("answer-selected", {
        activeQuestionIndex: this.cardIndex,
        selectedAnswerIndex: this.selectedAnswerIndex,
        questionScore: this.questionScore,
      });
    },
    answerClass(i) {
      if (
        i === this.correctAnswerIndex && // highlight correct answer
        (this.timeUp || i === this.selectedAnswerIndex)
      ) {
        return "correct";
      }
      if (
        i === this.selectedAnswerIndex || // highlight incorrect answers
        (this.timeUp && !this.answerSelected) // or all choices if they picked nothing
      ) {
        return "incorrect";
      }
    },
    handleAnswer(index) {
      // only call handleAnswer on the most recent card displayed
      // (avoid enabling key activity on inactive answer buttons)
      if (this.active) {
        if (
          (!this.timeUp && !this.answerSelected) ||
          (this.timeUp && index < 0) // avoid enabling key activity after time is up
        ) {
          if (index >= 0) {
            this.selectedAnswerIndex = index; // default value for an empty response is -1
          }
        }
      } else {
        console.warn('somehow managed to "handleAnswer" on an inactive q');
        debugger;
      }
    },
    advance() {
      // we may not need this "if" b/c the btn is just not rendered in that case,
      // but it doesn't hurt anything to leave it in and maybe adds clarity

      if (this.current && this.last) {
        this.$emit("last-clicked");
      }
      if (this.current) {
        this.$emit("next-clicked");
        window.removeEventListener("keydown", this.handleKeyDown);
      } else {
        console.warn('somehow managed to "advance" on old question');
        debugger;
      }
    },
    // todo: similar to the comment in the template, try to move this into the quiz view
    //       better to have a single event handler for the whole app than one for each q
    handleKeyDown(e) {
      if (this.active) {
        if (e.key == "1" || e.key === "2" || e.key === "3" || e.key === "4") {
          this.handleAnswer(this.shuffledAnswers[e.key - 1].index);
        }
      } else if (this.current) {
        // todo: use focus to manage this
        if (e.which === 13) {
          this.advance();
        }
      }
    },
  },
  async mounted() {
    window.addEventListener("keydown", this.handleKeyDown, { passive: true });

    // if this is the active question, maybe scroll it into view
    if (!this.selectedAnswer) {
      if (this.cardIndex === 0) {
        scrollElIntoView(this.$refs.question, { block: "startish-middle" });
        // the first question - scroll it somewhere between the middle and the start
      } else {
        // all other questions..
        const parentPositionInfo = await getParentPositionInfo();
        const currQ = this.$refs.question;
        if (currQ.offsetHeight > parentPositionInfo.viewportHeight) {
          // if there's not enough room to fit the whole next question, just scroll as much of it into view as possible
          scrollElIntoView(this.$refs.question, { block: "start" });
        } else {
          // scroll next questions to same position as previous question
          // kind of janky, the selector pokes a hole in the component encapsulation,
          // but i think it's what readers expect/want to happen
          // it would be cleaner if this were lifted up into a higher level component
          // that has jursidiction over the list of cards, but i'm content w/ this for now
          const prevQ = currQ.closest("li").previousElementSibling;
          if (prevQ) {
            if (prevQ.offsetTop + parentPositionInfo.iframeTop < 0) {
              // if the previous question actually starts outside the viewport,
              // just scroll as little as possible to get it fully into view
              scrollElIntoView(this.$refs.question, { block: "start" });
            } else {
              scrollParentBy({ top: prevQ.offsetHeight });
            }
          } else {
            console.warn("unexpectedly missing previous question for", currQ);
          }
        }
      }
    }
  },
};

// fisher-yates shuffle provided by co-pilot
function shuffle(array) {
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [array[i], array[j]] = [array[j], array[i]];
  }
  return array;
}
</script>

<style scoped lang="scss">
@import "../styles/when-dark-mode.scss";
ul {
  list-style-type: none;
  padding: 0;
}

.card {
  padding: 1em 0;
}

.question-header {
  text-transform: uppercase;
  font-size: 0.8em;
  margin: 0.3em 0;
  color: #666;
  color: #{"hsla(var(--txt-color-hsl), 0.7)"};
  position: relative;
  line-height: 1; /* to align the bracket, there are no descenders in this text */
}

.question-header::after {
  border-color: var(--theme-color);
  width: 10px;
  height: 10px;
  border-width: 4.35px;
  border-style: solid solid none none;
  content: "";
  display: inline-block;
  position: absolute;
  top: 0;
  right: 0;
}

.question-text {
  font-weight: bold;
  margin: 0 0 1em;
  font-size: 1.1em;
}

.answer-button {
  width: 100%;
  background-color: #f0f0f0;
  background-color: #{"hsla(var(--txt-color-hsl), 0.065)"};
  transition: background 0.12s ease;
  border: none;
  position: relative;

  padding: 0 1.2em; /* 2em is enough space for the check/cross indicating correctness */
  height: clamp(2.5em, 16vw, 5em);
}

.answer-button:enabled:hover,
.answer-button:enabled:focus {
  background-color: var(--gray-d-color);

  @include when-dark-mode {
    background-color: var(--raisin-light-20-color);
  }
}

.answer-button:disabled.correct {
  background-color: #e4f6d8;
  background-color: #{"hsl(var(--kiwi-hsl), 0.2)"};
  @include when-dark-mode {
    background-color: #{"hsl(var(--kiwi-hsl), 0.4)"};
  }
}

.answer-button:disabled.incorrect {
  background-color: #f6d5d5;
  background-color: #{"hsl(var(--partisan-red-hsl), 0.2)"};
  @include when-dark-mode {
    background-color: #{"hsl(var(--partisan-red-hsl), 0.5)"};
  }
}

.answers {
  margin-bottom: 1.6em;
}

.answers > li:not(:first-child) {
  margin-top: 4vw;
}

.answers > li svg {
  position: absolute;
  top: 0.4em;
  right: 0.4em;
}

.correctness {
  display: flex;
  align-items: center;
  margin-bottom: 0.8em;
}

.icon {
  margin-right: 0.3em;
}

@media (min-width: 30em) {
  .answers {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 1em;
  }

  .answers > li {
    margin: 0 !important;
  }
}

.next {
  display: flex;
  align-items: baseline;
  gap: 1em;
  margin-bottom: 2rem;

  &.last {
    flex-wrap: wrap-reverse;
  }
}

.score {
  flex-shrink: 0;
  margin-left: auto;
}

@media (max-width: 22em) {
  /* nudge the icon for very small screens */
  .answers > li svg {
    transform: scale(0.8);
    top: 0;
    right: 0;
  }
}
</style>
